import React, { useState, useEffect, useMemo } from 'react';

import makeDefaultExpression from '@/helpers/makeDefaultExpression';
import PlusCircle from '@/components/Icons/PlusCircle';
import IdentificationBadge from '@/components/Icons/IdentificationBadge';
import MinusCircle from '@/components/Icons/MinusCircle';
import type { Questionnaire } from '@/typings/Questionnaire';
import { Select, Input, Checkbox, TextArea, Checkboxes, NumberInput } from '@/components/DesignLibrary/Atoms';
import Button from '@/components/DesignLibrary/Button';
import { QuestionnaireAssigneeTemplate } from '@/typings/questionnaires-api-models';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import TabText from '@/components/DesignLibrary/Atoms/TabText';
import Buttons from '@/components/Buttons';
import PDFFillEditor from '@components/Questionnaire/questionnaireEditor/PDFFillEditor';
import removeNullish from '@/helpers/removeNullish';
import isNonMasterLinkedList from '@/helpers/questionnaires/isNonMasterLinkedList';
import getListOrTableField from '@helpers/getListOrTableField';
import { useFormEditorContext } from '../FormEditor/FormEditorContext';
import Conditions from '../Conditions';
import ListFilter from '../ListFilter';
import s from './FieldEditor.module.scss';
import ViewSelector from './ViewSelector';
import IsEditableInTableView from './IsEditableInTableView';
import IsViewableInTableView from './IsViewableInTableView';
import AutofillEditor from '../AutofillEditor';
import ColumnEditor from '../ColumnEditor';
import AllowUnknownValues from './AllowUnknownValues';

const fieldTypes = [
  {key: "short-text", label: "Short Text" },
  {key: "ssn", label: "Social Security Number" },
  {key: "long-text", label: "Long Text" },
  {key: "numeric", label: "Numeric" },
  {key: "number", label: "Number" },
  {key: "percent", label: "Percent" },
  {key: "currency", label: "Currency" },
  {key: "multi-select", label: "Checkboxes" },
  {key: "single-select", label: "Radio buttons" },
  {key: "selection", label: "Dropdown" },
  {key: "name", label: "Name" },
  {key: "date", label: "Date" },
  {key: "address-us", label: "US Address" },
  {key: "address-international", label: "International Address" },
  {key: "phone-number", label: "Phone Number" },
  {key: "explanation", label: "Explanation" },
  {key: "list", label: "List" },
  {key: "table", label: "Table" },
  {key: "median-income", label: "Median Income Widget"},
  {key: "monthly-income", label: "Monthly Income Ratio"},
  {key: "creditor-select", label: "Creditor Select"},
];

type FieldEditorProps = {
  field: Questionnaire.Field;
  usedKeys: string[];
  onUpdateField: (updatedField: Questionnaire.Field) => void;
  onRemoveField: (fieldId: string) => void;
  assigneeTemplates: QuestionnaireAssigneeTemplate[];
  onAddField: (listFieldTemplateId: string) => void;
  isInList: boolean;
  formData: Questionnaire.Questionnaire;
  currentSection: Questionnaire.Section;
  canSave: boolean;
  isSaving: boolean;
};

type FieldEditorTab = 'settings' | 'conditions' | 'list-filter' | 'autofill' | 'assignees' | 'AI' | 'columns' | 'pdf-fill';

type Expression = Pick<Questionnaire.Expression, 'expression' | 'filterExpression' | 'filterExpressionVariableName' | 'filterExpressionOperand' | 'filterExpressionItemSelector' | 'filterExpressionReducer'> & {
  variables: Pick<Questionnaire.ExpressionVariable, 'variableName' | 'referenceId' | 'referenceType' | 'selector'>[];
};

const fieldKeyRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

const makeDefaultReferenceList = (): NonNullable<Questionnaire.Field['referencedList']> => ({
  crossReferenceSubFieldKey: '',
  fieldKey: '',
  fieldLabel: '',
  isReferencingExistingList: false,
  subFieldKey: '',
  subFieldLabel: '',
});

const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  usedKeys,
  onUpdateField,
  onRemoveField,
  onAddField,
  assigneeTemplates,
  isInList,
  formData,
  currentSection,
  canSave,
  isSaving,
}) => {
  const { selectedFieldEditorTab, fieldsById } = useFormEditorContext();
  const useEditableInTableView = useFeatureFlag('questionnaire-editable-table-view');

  // Make sure that everything below is set correctly in the useEffect(() => [field])
  const [autofill, setAutofill] = useState(field.autofill);
  const [label, setLabel] = useState<string>(field.label);
  const [key, setKey] = useState<string>(field.key);
  const [description, setDescription] = useState<string>(field.description);
  const [deduplicationLogic, setDeduplicationLogic] = useState<string>(field.aiDeduplicationLogic ?? '');
  const [type, setType] = useState<Questionnaire.Field['type']>(field.type);
  const [linkedFieldTemplates, setLinkedFieldTemplates] = useState<{
    fieldId: string;
    fieldIndex: number | null;
    linkedFieldIndex: number | null;
  }[]>(field.linkedFields?.map((f) => ({
    fieldId: f.linkedFieldTemplateId,
    fieldIndex: f.fieldTemplateIndex,
    linkedFieldIndex: f.linkedFieldTemplateIndex,
  })) ?? []);
  const [placeholder, setPlaceholder] = useState<string | undefined>(field.placeholder);
  const [hint, setHint] = useState<string | undefined>(field.hint);
  const [isRequired, setIsRequired] = useState<boolean>(field.isRequired);
  const [isTitleField, setIsTitleField] = useState<boolean>(field.isTitleField);
  const [isSubtitleField, setIsSubtitleField] = useState<boolean>(field.isSubtitleField);
  const [assignedTo, setAssignedTo] = useState<string[]>(field.assignedTo);
  const [view, setView] = useState<string | null>(field.view ?? field.type === 'list' ? 'table' : null);
  const [isEditableInTableView, setIsEditableInTableView] = useState(field.isEditableInTableView);
  const [isViewableInTableView, setIsViewableInTableView] = useState(field.isViewableInTableView);
  const [allowUnknownValues, setAllowUnknownValues] = useState(field.allowUnknownValues);
  const [options, setOptions] = useState<NonNullable<Questionnaire.Field['options']>>(Array.isArray(field.options) ? field.options : []);
  const [selectedTab, setSelectedTab] = useState<FieldEditorTab>(selectedFieldEditorTab);
  const [subsection, setSubsection] = useState(field.subsection);
  const [listSubsection, setListSubsection] = useState(field.listSubsection);
  const [conditionExpression, setConditionExpression] = useState<Expression>(field.conditionExpression || makeDefaultExpression());
  const [listFilterExpression, setListFilterExpression] = useState<Expression>(field.listFilterExpression || makeDefaultExpression());
  const [referenceList, setReferenceList] = useState<boolean>(field.referencedList?.isReferencingExistingList || false);
  const [referenceListConfig, setReferenceListConfig] = useState<NonNullable<Questionnaire.Field['referencedList']>>(field.referencedList || makeDefaultReferenceList());
  // Make sure that everything above is set correctly in the useEffect(() => [field])

  // Local state
  const [availableListFields, setAvailableListFields] = useState<{ id: string; key: string; label: string, type: string}[]>([]);
  const [selectedListFieldKey, setSelectedListFieldKey] = useState<string>(field.referencedList?.fieldKey || "");
  const [availableSubFields, setAvailableSubFields] = useState<{ id: string; key: string; label: string, type: string}[]>([]);
  const [selectedSubFieldKey, setSelectedSubFieldKey] = useState<string>(field.referencedList?.subFieldKey || "");
  const [currentListAvailableSubFields, selectCurrentListAvailableSubFields] = useState<{
    id: string,
    key: string,
    label: string,
    type: string,
  }[]>([]);
  const [selectedCrossReferenceSubFieldKey, setSelectedCrossReferenceSubFieldKey] = useState<string>(field.referencedList?.crossReferenceSubFieldKey || "");

  const isUsedKey = useMemo(() => usedKeys.includes(key), [usedKeys, key]);
  const isValidKey = useMemo(() => fieldKeyRegex.test(key), [key]);

  useEffect(() => {
    setAutofill(field.autofill);
    setLabel(field.label);
    setKey(field.key);
    setDescription(field.description);
    setDeduplicationLogic(field.aiDeduplicationLogic ?? '');
    setType(field.type);
    setPlaceholder(field.placeholder ?? '');
    setHint(field.hint ?? '');
    setIsRequired(field.isRequired);
    setIsTitleField(field.isTitleField);
    setIsSubtitleField(field.isSubtitleField);
    setAssignedTo(field.assignedTo);
    setView(field.view);
    setLinkedFieldTemplates(field.linkedFields?.map((f) => ({
      fieldId: f.linkedFieldTemplateId,
      fieldIndex: f.fieldTemplateIndex,
      linkedFieldIndex: f.linkedFieldTemplateIndex,
    })) ?? []);
    setIsEditableInTableView(field.isEditableInTableView);
    setIsViewableInTableView(field.isViewableInTableView);
    setAllowUnknownValues(field.allowUnknownValues);
    setOptions(Array.isArray(field.options) ? field.options : []);
    setSubsection(field.subsection);
    setListSubsection(field.listSubsection);
    setConditionExpression(field.conditionExpression || makeDefaultExpression());
    setListFilterExpression(field.listFilterExpression ?? makeDefaultExpression());
    setReferenceList(field.referencedList?.isReferencingExistingList || false);
    setReferenceListConfig(field.referencedList || makeDefaultReferenceList());
  }, [field]);

  const needPlaceholder = useMemo(() =>
    [
      "number",
      "selection",
      "short-text",
      "long-text",
      "numeric",
      "percent",
      "currency",
      "date",
    ].includes(type),
  [type]);

  const labelError: string | undefined = useMemo(() => {
    if (!label) {
      return 'Required';
    }

    return undefined;
  }, [label]);

  const keyError: string | undefined = useMemo(() => {
    if (!key) {
      return 'Required';
    }

    if (isUsedKey) {
      return 'Key already used';
    }

    if (!isValidKey) {
      return 'Key can contain only a-z, A-Z, 0-9 and _. Key can\'t begin with numbers';
    }

    return undefined;
  }, [key, isValidKey, isUsedKey]);

  // Prevent list being nested
  const availableFieldTypes = useMemo(() => {
    const isInTableOrList = (currentField: Questionnaire.Field): boolean => {
      if (field.type === 'list' || field.type === 'table') {
        return false;
      }
      if (currentField.type === 'list' || currentField.type === 'table') {
        return true;
      }
      if (!currentField.listFieldTemplateId) {
        return false;
      }
      const allFields: Record<string, Questionnaire.Field> = currentSection.fields.reduce((acc, val) => ({
        ...acc,
        [val.id]: val,
      }), {});
      const parentField = allFields[currentField.listFieldTemplateId];
      if (parentField) {
        return isInTableOrList(parentField);
      }
      return false;
    };
    const hasTableOrListParent = isInTableOrList(field);
    if (hasTableOrListParent) {
      return fieldTypes.filter(({ key: fieldKey }) => fieldKey !== 'list' && fieldKey !== 'table');
    }
    return fieldTypes;
  }, [field, currentSection]);

  // List of possible fields to link to (not self, same type, and not already linked)
  const availableLinkedFields = useMemo(() => {
    const opts = formData.sections.flatMap((section) =>
      section.fields
        .filter(
          (f) =>
            f.type === type
            && f.id !== field.id,
        )
        .filter((f) => {
          if (f.listFieldTemplateId) {
            const listField = fieldsById[f.listFieldTemplateId];
            const isLinkedListChild = isNonMasterLinkedList(listField);
            if (isLinkedListChild) {
              return false;
            }
            return true;
          }
          return true;
        })
        .map((f) => ({
          description: section.label,
          key: f.id,
          label: f.label,
        }))
        .filter(removeNullish),
    );

    // Only add "No linked field" if linkedFieldTemplateId exists
    if (linkedFieldTemplates.length > 0) {
      opts.unshift({ description: 'Remove linked field', key: '', label: 'No linked field' });
    }

    return opts;
  }, [formData, fieldsById, type, field.id, linkedFieldTemplates]);

  const handleSave = (): void => {
    if (!keyError && !labelError) {
      const listFieldTemplateId = field.listFieldTemplateId
        ? fieldsById[field.listFieldTemplateId]?.linkedFields
          ?.find((f) => f.fieldTemplateId === field.listFieldTemplateId)?.linkedFieldTemplateId ?? field.listFieldTemplateId
        : null;
      const updatedField = {
        ...field,
        aiDeduplicationLogic: deduplicationLogic,
        allowUnknownValues,
        assignedTo,
        autofill,
        conditionExpression,
        description,
        hint,
        isEditableInTableView,
        isRequired: type === 'explanation' ? false : isRequired,
        isSubtitleField,
        isTitleField,
        isViewableInTableView,
        key,
        label,
        linkedFieldTemplates: linkedFieldTemplates.filter((i) => i?.fieldId),
        listFieldTemplateId,
        listFilterExpression,
        listSubsection,
        options,
        placeholder,
        referencedList: referenceListConfig,
        subsection,
        type,
        view,
      };
      onUpdateField(updatedField as Questionnaire.Field);
    }
  };

  const handleAddOption = (): void => {
    setOptions([...options, { isDefault: false, key: '', label: '', pdfFillKey: '' }]);
  };

  const handleOptionChange = (index: number, fieldKey: string, value: string | boolean): void => {
    const updatedOptions = options.map((option, i) => {
      if (i === index) {
        return { ...option, [fieldKey]: value };
      } if (fieldKey === 'isDefault' && value === true) {
        return { ...option, isDefault: false };
      }
      return option;
    });
    setOptions(updatedOptions);
  };

  const handleRemoveOption = (index: number): void => {
    if (window.confirm('Are you sure you want to remove this option?')) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const handleAssignedToChange = (ids: string[]): void => {
    setAssignedTo(ids);
  };

  const tabs = useMemo(() => [
    { key: 'settings', label: 'Settings', selected: selectedTab === 'settings' },
    { key: 'conditions', label: 'Conditions', selected: selectedTab === 'conditions' },
    ...field.type === 'list' ? [{ key: 'list-filter', label: 'Filters', selected: selectedTab === 'list-filter' }] : [],
    { key: 'autofill', label: 'Autofill', selected: selectedTab === 'autofill' },
    { key: 'AI', label: 'AI', selected: selectedTab === 'AI' },
    ...!field.listFieldTemplateId && formData.assigneeTemplates.length > 0 ? [{ key: 'assignees', label: 'Assignees', selected: selectedTab === 'assignees' }] : [],
    ...field.type === 'table' ? [{ key: 'columns', label: 'Columns', selected: selectedTab === 'columns' }] : [],
    { key: 'pdf-fill', label: 'PDF Fill', selected: selectedTab === 'pdf-fill' },
  ], [selectedTab, formData.assigneeTemplates.length, field.type, field.listFieldTemplateId]);

  useEffect(() => {
    if (selectedTab === 'columns' && field.type !== 'table') {
      setSelectedTab('settings');
    }
  }, [selectedTab, field]);

  const extractListFields = (questionnaireData: Questionnaire.Questionnaire): Array<{ id: string; key: string; label: string; type: string }> => {
    const listFields: Array<{ id: string; key: string; label: string, type: string}> = [];

    questionnaireData.sections.forEach((section: Questionnaire.Section) => {
      section.fields.forEach((item: Questionnaire.Field) => {
        if (item.type === 'list' && (item?.listItemTemplates ?? []).length > 0) {
          listFields.push({
            id: item.id,
            key: item.key,
            label: item.label,
            type: item.type,
          });
        }
      });
    });
    return listFields;
  };

  useEffect(() => {
    if (referenceList) {
      setAvailableListFields(extractListFields(formData));
    } else {
      setReferenceList(false);
      setAvailableListFields([]);
      setSelectedListFieldKey('');
      setSelectedSubFieldKey('');
      setReferenceListConfig(makeDefaultReferenceList());
    }
  }, [referenceList, formData]);

  useEffect(() => {
    if (field.referencedList?.isReferencingExistingList) {
      setAvailableListFields(extractListFields(formData));
      setReferenceList(true);
      setSelectedListFieldKey(field.referencedList.fieldKey);
      setSelectedSubFieldKey(field.referencedList.subFieldKey);
      setSelectedCrossReferenceSubFieldKey(field.referencedList.crossReferenceSubFieldKey ?? '');
    }
  }, [field.referencedList, formData]);

  useEffect(() => {
    setSelectedTab(selectedFieldEditorTab);
  }, [selectedFieldEditorTab]);

  useEffect(() => {
    const subListFields: Array<{ id: string; key: string; label: string, type: string}> = [];
    if (referenceList) {
      formData.sections.forEach((section: Questionnaire.Section) => {
        section.fields.forEach((fieldData: Questionnaire.Field) => {
          if (fieldData.key === selectedListFieldKey) {
            (fieldData?.listItemTemplates || []).forEach((item: Questionnaire.Field) => {
              if(item.type === 'short-text' || item.type === 'long-text') {
                subListFields.push({
                  id: item.id,
                  key: item.key,
                  label: item.label,
                  type: item.type,
                });
              }
            });
          }
        });
      });
    }
    setAvailableSubFields(subListFields);
  }, [selectedListFieldKey, formData.sections, referenceList]);

  useEffect(() => {
    const currentListSubFields: Array<{ id: string; key: string; label: string, type: string}> = [];
    if (referenceList) {
      formData.sections.forEach((section: Questionnaire.Section) => {
        section.fields.forEach((fieldData: Questionnaire.Field) => {
          if (fieldData.id === field.listFieldTemplateId) {
            (fieldData?.listItemTemplates || []).forEach((item: Questionnaire.Field) => {
              if(item.type === 'short-text' || item.type === 'long-text') {
                currentListSubFields.push({
                  id: item.id,
                  key: item.key,
                  label: item.label,
                  type: item.type,
                });
              }
            });
          }
        });
      });
    }
    selectCurrentListAvailableSubFields(currentListSubFields);
  }, [field.listFieldTemplateId, formData.sections, referenceList]);

  useEffect(() => {
    if (selectedSubFieldKey.length > 0 && selectedListFieldKey.length > 0) {
      const selectedList = availableListFields.find(list => list.key === selectedListFieldKey);
      const selectedSubField = availableSubFields.find(list => list.key === selectedSubFieldKey);
      setReferenceListConfig({
        crossReferenceSubFieldKey: selectedCrossReferenceSubFieldKey,
        fieldKey: selectedList?.key || '',
        fieldLabel: selectedList?.label || '',
        isReferencingExistingList: true,
        subFieldKey: selectedSubField?.key || '',
        subFieldLabel: selectedSubField?.label || '',
      });
    }
  }, [selectedSubFieldKey, availableListFields, availableSubFields, selectedListFieldKey, selectedCrossReferenceSubFieldKey]);

  const isInListOrTable = (fieldId: string): boolean => {
    const currentField = fieldsById[fieldId];
    if (['list', 'table'].includes(currentField?.type)) {
      return true;
    }
    if (currentField?.listFieldTemplateId) {
      return isInListOrTable(currentField?.listFieldTemplateId);
    }
    return false;
  };

  return (
    <div className={s.fieldEditor}>
      <div className={s.fieldHeader}>
        <IdentificationBadge />
        <span className={s.label}>{field.label}</span>
      </div>

      <div className={s.fieldContent}>
        <div className={s.fieldContentTabs}>
          {tabs.map(tab => <TabText {...tab} key={tab.key} onClick={() => setSelectedTab(tab.key as FieldEditorTab)} />)}
        </div>
        {selectedTab === 'conditions' && <Conditions
          conditionExpression={conditionExpression}
          disabled={linkedFieldTemplates.length > 0}
          setConditionExpression={setConditionExpression}
        />}
        {selectedTab === 'list-filter' && <ListFilter
          listFilterExpression={listFilterExpression}
          disabled={linkedFieldTemplates.length > 0}
          setListFilterExpression={setListFilterExpression}
        />}
        {selectedTab === 'autofill' && (
          <AutofillEditor
            autofill={autofill}
            fieldKey={field.key}
            onChange={setAutofill}
            questionnaireTemplateId={formData.id}
          />
        )}
        {selectedTab === 'settings' && <>
          <Select
            label=""
            description="Field type"
            disabled={linkedFieldTemplates.length > 0}
            options={availableFieldTypes}
            value={type}
            onChange={(v) => setType(v as Questionnaire.Field['type'])}
            optional
          />
          {type !== 'explanation' && <Checkbox
            label="Required"
            isChecked={isRequired}
            onChange={setIsRequired}
            size="sm"
          />}
          {field.type !== 'list' && field.type !== 'table' && getListOrTableField({ fieldId: field.id, fieldsById }) && (
            <>
              <Checkbox
                label="Is Title Field"
                isChecked={isTitleField}
                onChange={setIsTitleField}
                size="sm"
              />
              <Checkbox
                label="Is Subtitle Field"
                isChecked={isSubtitleField}
                onChange={setIsSubtitleField}
                size="sm"
              />
            </>
          )}
          <ViewSelector
            fieldType={field.type}
            onChange={setView}
            value={view}
          />
          {useEditableInTableView && <IsEditableInTableView
            isEditableInTableView={isEditableInTableView}
            onChange={setIsEditableInTableView}
            isInList={isInList}
          />}
          <IsViewableInTableView
            isViewableInTableView={isViewableInTableView}
            onChange={setIsViewableInTableView}
            isInList={isInList}
          />
          {
            field.type === 'currency' && (
              <AllowUnknownValues
                allowUnknownValues={allowUnknownValues}
                onChange={setAllowUnknownValues}
              />
            )
          }
          <div className={s.separator} />
          <Input
            label=""
            description="Label"
            value={label}
            error={labelError}
            onChange={setLabel}
            optional
          />
          <TextArea
            label=""
            description="Description"
            value={description}
            onChange={setDescription}
            optional
          />
          {needPlaceholder && <Input
            optional
            label=""
            description="Placeholder"
            value={placeholder}
            onChange={setPlaceholder}
          />}
          {/* <Input
            optional
            label=""
            description="Hint message"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
          /> */}
          <Input
            label=""
            description="Field name"
            value={key}
            error={keyError}
            onChange={setKey}
            optional
          />
          {(currentSection.subsections || field.subsection) && (field.type === 'list' || field.type === 'table' || !getListOrTableField({ fieldId: field.id, fieldsById })) && (
            <Select
              label=''
              description='Subsection'
              options={[
                ...(currentSection.subsections?.map((section) => ({
                  key: section,
                  label: section,
                })) || []),
                ...(field.subsection && !(currentSection.subsections || []).includes(field.subsection)
                  ? [{ key: field.subsection, label: field.subsection }]
                  : []),
                {
                  key: '',
                  label: 'none',
                },
              ]}
              onChange={val => setSubsection(val || null)}
              value={subsection}
              optional
            />
          )}
          {getListOrTableField({ fieldId: field.id, fieldsById }) && field.type !== 'list' && field.type !== 'table' && (
            <Input
              label=""
              description="List subsection"
              value={listSubsection}
              onChange={newSubsection => setListSubsection(newSubsection || null)}
              optional
            />
          )}
          <Select
            label=""
            description="Linked field"
            options={availableLinkedFields}
            value={linkedFieldTemplates[0]?.fieldId}
            onChange={(v) => setLinkedFieldTemplates([{ fieldId: v, fieldIndex: null, linkedFieldIndex: null }])}
            optional
          />
          {linkedFieldTemplates[0]?.fieldId && isInListOrTable(linkedFieldTemplates[0]?.fieldId) && (
            <>
              <NumberInput
                min={0}
                description='Index of linked field'
                value={linkedFieldTemplates[0]?.linkedFieldIndex}
                onChange={(v) => setLinkedFieldTemplates([{ ...linkedFieldTemplates[0], linkedFieldIndex: v }])}
              />
            </>
          )}
          {isInListOrTable(field.id) && (
            <>
              <NumberInput
                min={0}
                description='Index of this field'
                value={linkedFieldTemplates[0]?.fieldIndex}
                onChange={(v) => setLinkedFieldTemplates([{ ...linkedFieldTemplates[0], fieldIndex: v }])}
              />
            </>
          )}
          {['multi-select', 'single-select', 'selection'].includes(type) && (
            <div className={s.options}>
              <span className='caption'>Options</span>
              {field.type === 'selection' && <Checkbox
                label="Reference Existing List"
                isChecked={referenceList}
                onChange={() => setReferenceList(!referenceList)}
                size="sm"
              />}
              {referenceList && (
                <Select
                  label=""
                  description="Select List"
                  options={availableFieldTypes
                    ? availableListFields.map(listField => ({
                      key: listField.key,
                      label: listField.label,
                    }))
                    : []}
                  value={selectedListFieldKey}
                  onChange={(v) => { setSelectedListFieldKey(v); }}
                  optional
                />
              )}
              {referenceList && availableSubFields.length > 0 && <Select
                label=""
                description="Select Title Field"
                options={availableSubFields || []}
                value={selectedSubFieldKey}
                onChange={(v) => { setSelectedSubFieldKey(v); }}
                optional
              />}
              {referenceList && currentListAvailableSubFields.length > 0 && <Select
                label=""
                description="Select Cross Reference Title Field"
                options={currentListAvailableSubFields || []}
                value={selectedCrossReferenceSubFieldKey}
                onChange={(v) => { setSelectedCrossReferenceSubFieldKey(v); }}
                optional
              />}
              {!referenceList && options.map((option, index) => (
                <div key={index} className={s.optionContainer}>
                  <div className={s.option}>
                    <Input
                      label=""
                      description="Key"
                      value={option.key}
                      onChange={(value) => handleOptionChange(index, 'key', value)}
                      optional
                    />
                    <Input
                      label=""
                      description="Label"
                      value={option.label}
                      onChange={(value) => handleOptionChange(index, 'label', value)}
                      optional
                    />
                  </div>
                  <Input
                    label=''
                    description='PDF Fill Key'
                    value={option.pdfFillKey ?? ''}
                    onChange={(value) => handleOptionChange(index, 'pdfFillKey', value)}
                    optional
                  />
                  <Checkbox
                    label="Set as default"
                    isChecked={option.isDefault}
                    onChange={(value) => handleOptionChange(index, 'isDefault', value)}
                    size="sm"
                  />
                  <Buttons isWrapper className={s.removeOptionButton} onClick={() => handleRemoveOption(index)}>
                    <MinusCircle />
                  </Buttons>
                </div>
              ))}
              {!referenceList && <button
                className={s.addOptionButton}
                onClick={handleAddOption}
              >
                <PlusCircle weight="fill" /> Add Option
              </button>}
            </div>
          )}
          {(field.type === 'list' || field.type === 'table' || field.type === 'explanation') && field.key && (
            <button
              className={s.addOptionButton}
              onClick={() => onAddField(field.id)}
            >
              <PlusCircle weight="fill" /> Add Field
            </button>
          )}
          {/* {['multi-select', 'single-select'].includes(type) && (
            <div>
              <h3>Option Style</h3>
              <select value={optionStyle} onChange={(e) => setOptionStyle(e.target.value as SelectOptionStyle)}>
                <option value="checkbox">Checkbox</option>
                <option value="radio-button">Radio Button</option>
                <option value="button">Button</option>
              </select>
            </div>
          )} */}
        </>}
        {selectedTab === 'columns' && field.type === 'table' && <ColumnEditor field={field} onUpdateField={onUpdateField} />}
        {selectedTab === 'assignees' && <Checkboxes
          label=""
          description="Assignee"
          options={assigneeTemplates.map((assignee) => ({ key: assignee.id, label: assignee.label }))}
          value={assignedTo}
          onChange={handleAssignedToChange}
          compact
        />}
        {selectedTab === 'AI' && (
          <TextArea
            label=''
            description='Deduplication logic'
            value={deduplicationLogic}
            onChange={setDeduplicationLogic}
            optional
          />
        )}
        {selectedTab === 'pdf-fill' && (
          <PDFFillEditor field={field} />
        )}
        {selectedTab !== 'columns' && selectedTab !== 'pdf-fill' && (
          <div className={s.buttons}>
            <Button loading={isSaving} disabled={!canSave} size="sm" variant="primary" onClick={handleSave}>Save Field</Button>
            {selectedTab === 'settings' && <Button size="sm" variant="destructive" onClick={() => {
              if (window.confirm('Are you sure you want to delete this field?')) {
                onRemoveField(field.id);
              }
            }}>Delete</Button>}
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldEditor;
